
import { toast } from "@/hooks/use-toast";
import { convertToWebP } from "./convertUtils";
import { getAuthHeader } from "@/services/authService";

/**
 * Uploads an image to Cloudflare-backed storage and returns the public URL.
 * @param file The file to upload
 * @param folder Optional folder path within the bucket
 * @returns The public URL of the uploaded image
 */
export const uploadImage = async (file: File, folder: string = "blog"): Promise<string> => {
  try {
    // Convert image to WebP format first
    const webpFile = await convertToWebP(file);

    const formData = new FormData();
    formData.append('file', webpFile);
    formData.append('folder', folder);

    const response = await fetch('/api/admin/upload-image', {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      toast({
        title: "Image upload failed",
        description: errorBody.error || "There was a problem uploading your image",
        variant: "destructive",
      });
      throw new Error(errorBody.error || "Upload failed");
    }

    const data = await response.json();
    return data.url;
  } catch (error: any) {
    console.error('Error uploading image:', error);
    toast({
      title: "Image upload failed",
      description: error.message || "There was a problem uploading your image",
      variant: "destructive",
    });
    throw error;
  }
};

/**
 * Uploads a file to Cloudflare-backed storage.
 * @param file The file to upload
 * @param folder The folder to upload to
 * @returns The public URL of the uploaded file
 */
export const uploadToStorage = async (file: File, folder: string = "blog_images/inline"): Promise<string> => {
  try {
    // Try to convert to WebP for better compression
    let webpFile: File;
    try {
      webpFile = await convertToWebP(file);
    } catch (error) {
      console.warn("WebP conversion failed, using original file:", error);
      webpFile = file;
    }
    
    const extension = webpFile.type.includes('webp') ? 'webp' : file.name.split('.').pop() || 'jpg';

    const formData = new FormData();
    formData.append('file', webpFile);
    formData.append('folder', folder);

    const response = await fetch('/api/admin/upload-image', {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || "Upload failed");
    }

    const data = await response.json();
    return data.url;
  } catch (error: any) {
    console.error("Error uploading to storage:", error);
    throw error;
  }
};
